// Android's floating bubble and correction popup (FloatingOverlay.kt /
// CorrectionPopupOverlay.kt) are plain android.webkit.WebViews, not
// Tauri-managed ones - Android overlay windows can only host a raw WebView,
// not a second Tauri window. window.AndroidBridge is built below on top of an
// origin-restricted message channel (SecureBridge.kt - it replaced
// addJavascriptInterface, which Play rejected) and is what every "are we in the
// Android overlay" check in the shared desktop/Android components keys off of -
// it's a different signal from isTauri()/__TAURI_INTERNALS__, which is absent
// here too since there's no Tauri runtime in a plain WebView.
declare global {
  interface Window {
    AndroidBridge?: {
      /** Tapping the floating bubble - shows the correction popup for the last detected selection. */
      showCorrectionPopup?: () => void;
      /** Writes text back into the app the current selection came from. */
      replace: (text: string) => void;
      /** Copies text to the system clipboard. */
      copy: (text: string) => void;
      /** Closes the correction popup overlay. */
      close: () => void;
      /** Brings the main app Activity to the foreground. */
      openApp?: () => void;
      /** Moves the correction popup overlay window by a raw-pixel delta (drag-to-move). */
      moveBy?: (dx: number, dy: number) => void;
      /** Pauses KalimatAI for the app the current selection came from - touch equivalent of desktop's hover-reveal pause button. */
      pauseCurrentApp?: (hours: number) => void;
      /** Opens the mobile equivalent of the desktop tray menu (pause options, open app, accessibility settings). */
      showQuickMenu?: () => void;
      /** Brings the app forward on Settings -> Allowed apps (the gear beside the bubble). */
      openAllowedAppsSettings?: () => void;
      /**
       * Voice in the correction popup - the same native recognizer and text-to-speech engine
       * the main app uses, reached directly because this WebView has no Tauri runtime.
       * Events come back through window.__kalimatVoiceEvent. Only present in
       * CorrectionPopupOverlay's WebView; use voiceService rather than calling these.
       */
      voiceIsRecognitionAvailable?: () => Promise<boolean>;
      voiceHasMicrophonePermission?: () => Promise<boolean>;
      /** Closes the popup and opens the app to ask for the microphone - an overlay cannot. */
      voiceRequestMicrophonePermission?: () => void;
      voiceStartListening?: (language: string) => void;
      voiceStopListening?: () => void;
      voiceSpeak?: (text: string, language: string) => void;
      voiceStopSpeaking?: () => void;
      /** Grows/shrinks the native overlay window to fit the pause/menu mini buttons - see FloatingOverlay.kt's resizeForExpansion(). */
      setExpanded?: (expanded: boolean) => void;
      /**
       * Drag-to-move-or-hide for the floating bubble. dragStart puts the drop target on
       * screen, dragBy moves the native window by a raw-pixel delta, and dragEnd either
       * leaves it where it was dropped or hides it (see FloatingOverlay.kt's finishDrag).
       * Only present in FloatingOverlay's WebView.
       */
      dragStart?: () => void;
      dragBy?: (dx: number, dy: number) => void;
      dragEnd?: () => void;
      /**
       * Makes the correction popup taller or shorter by a raw-pixel delta (negative grows
       * it), keeping the sheet's bottom edge where it is. Only present in
       * CorrectionPopupOverlay's WebView.
       */
      resizeSheetBy?: (dy: number) => void;
      /** Tapping the screenshot "Attach" pill - hands the detected screenshot to the correction popup in Ask mode. Only present in ScreenshotToolButtonOverlay's WebView. */
      attachImage?: () => void;
      /** Reads (and clears) the screenshot base64 handed off by ScreenshotToolButtonOverlay - in-memory transport, not the popup URL. Only present in CorrectionPopupOverlay's WebView. */
      getPendingImage?: () => Promise<string | null>;
      /**
       * Toggles the correction popup window between its default FLAG_NOT_FOCUSABLE state (so
       * outside taps pass through while the user is just reading corrections) and a genuinely
       * focusable state that can receive the soft keyboard - FLAG_NOT_FOCUSABLE blocks IME
       * entirely, so without this the chat "ask" field's Textarea could never open the
       * keyboard. Call with true on focus, false on blur - see CorrectionPopup.tsx's
       * onAskFieldFocusChange. Only present in CorrectionPopupOverlay's WebView.
       */
      setInputFocusable?: (focusable: boolean) => void;
      /**
       * Runs one local writing-check request in Rust and answers asynchronously via
       * window.__kalimatLocalCheckResolve. The overlay has no Tauri IPC, so this is how it
       * reaches the same grammar/spelling engines the desktop popup calls with invoke().
       * Only present in CorrectionPopupOverlay's WebView. Use androidLocalCheck() below
       * rather than calling this directly.
       */
      localCheck?: (requestId: string, request: string) => void;
    };
    /** Installed below; Kotlin calls it with the response for a given request id. */
    __kalimatLocalCheckResolve?: (requestId: string, response: string) => void;
    /**
     * Installed by FloatingButton.tsx; FloatingOverlay.kt calls it to say whether the
     * bubble is marking a selection or a field being typed in. The desktop equivalent is
     * the `floating-button-mode` Tauri event, which this overlay has no way to receive.
     */
    __kalimatSetButtonMode?: (mode: string, issueCount: number) => void;
    /**
     * Installed by CorrectionPopup.tsx; CorrectionPopupOverlay.kt calls it after making the
     * overlay window focusable, so the focused field asks for the keyboard a second time -
     * the first request was made while the window could not host an IME at all.
     */
    __kalimatRefocusInput?: () => void;
    /**
     * Installed by voiceService.ts in the popup overlay; CorrectionPopupOverlay.kt calls it
     * with each native voice event - "listen" for the microphone, "speak" for read-aloud.
     */
    __kalimatVoiceEvent?: (
      kind: 'listen' | 'speak',
      event: { type: string; text?: string; isFinal?: boolean; code?: string; message?: string },
    ) => void;
  }
}

// ---------------------------------------------------------------------------
// Building window.AndroidBridge on top of the origin-restricted channel
// ---------------------------------------------------------------------------

/**
 * What SecureBridge.kt installs instead of the old addJavascriptInterface object: a message
 * channel that only our own origin can talk to, plus the list of methods this particular
 * overlay offers. Play rejected the unrestricted bridge (JavaScript Interface Injection) -
 * see SecureBridge.kt for the whole story.
 */
interface BridgeChannel {
  postMessage(message: string): void;
  onmessage: ((event: { data: string }) => void) | null;
}

interface BridgeSpec {
  methods: string[];
  /** The subset that answer - they return a Promise here, since a channel can't reply inline. */
  returning: string[];
}

/**
 * Rebuilds window.AndroidBridge from the channel so every caller keeps using the same method
 * names. Built from the spec rather than as a catch-all Proxy on purpose: code asks "does this
 * overlay have voiceSpeak?" by checking for the method, and a proxy would answer yes to
 * everything - the voice path would then read a microphone permission that is not there.
 *
 * Runs before isAndroidOverlay below, in the same module, so that check still sees the bridge
 * the moment anything imports it - the channel and the spec are both in place before the page's
 * own scripts run (document start, SecureBridge.install).
 */
const installChannelBridge = () => {
  if (typeof window === 'undefined' || window.AndroidBridge) return;
  const w = window as unknown as { AndroidBridgeChannel?: BridgeChannel; __kalimatBridgeSpec?: BridgeSpec };
  const channel = w.AndroidBridgeChannel;
  const spec = w.__kalimatBridgeSpec;
  if (!channel || !spec) return;

  const replies = new Map<number, (value: unknown) => void>();
  let nextId = 0;
  channel.onmessage = (event) => {
    try {
      const { id, r } = JSON.parse(event.data) as { id: number; r: unknown };
      const resolve = replies.get(id);
      if (!resolve) return;
      replies.delete(id);
      resolve(r);
    } catch {
      // Not a reply of ours.
    }
  };

  const returning = new Set(spec.returning);
  const bridge: Record<string, (...args: unknown[]) => unknown> = {};
  for (const name of spec.methods) {
    bridge[name] = returning.has(name)
      ? (...args) =>
          new Promise((resolve) => {
            const id = ++nextId;
            replies.set(id, resolve);
            channel.postMessage(JSON.stringify({ m: name, a: args, id }));
          })
      : (...args) => {
          channel.postMessage(JSON.stringify({ m: name, a: args }));
        };
  }
  window.AndroidBridge = bridge as unknown as Window['AndroidBridge'];
};

installChannelBridge();

export const isAndroidOverlay = typeof window !== 'undefined' && !!window.AndroidBridge;

// ---------------------------------------------------------------------------
// Local writing check over the JNI bridge
// ---------------------------------------------------------------------------

/**
 * The correction popup overlay is a plain WebView, so it has no Tauri invoke(). The local
 * writing check lives in Rust all the same, and Kotlin exposes it through
 * AndroidBridge.localCheck (see LocalCheck.kt and src-tauri/src/android_jni.rs).
 *
 * That call can't return a value - the work happens on a background thread, because
 * blocking a @JavascriptInterface method would freeze the popup while a dictionary loads.
 * So each request carries an id, and Kotlin calls the resolver below when it's done. This
 * turns the pair back into a promise, so callers look the same as the invoke() path.
 */
type PendingResolver = (response: string) => void;

const pendingLocalChecks = new Map<string, PendingResolver>();
let nextLocalCheckId = 0;

if (typeof window !== 'undefined') {
  window.__kalimatLocalCheckResolve = (requestId, response) => {
    const resolve = pendingLocalChecks.get(requestId);
    if (!resolve) return; // already timed out, or a stale reply after a reload
    pendingLocalChecks.delete(requestId);
    resolve(response);
  };
}

/**
 * How long to wait before giving up on a request. Generous because downloading a dictionary
 * is several megabytes over whatever connection the phone has; a check itself is milliseconds.
 * Without this a lost reply would leave the caller's promise pending forever, and the UI
 * would sit on a spinner with no way back.
 */
const LOCAL_CHECK_TIMEOUT_MS = 120_000;

interface LocalCheckResponse<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

/** Send one request to the Rust writing check and resolve with its `data`, or throw. */
export const androidLocalCheck = <T>(request: Record<string, unknown>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const bridge = window.AndroidBridge;
    if (!bridge?.localCheck) {
      reject(new Error('the local writing check is not available in this WebView'));
      return;
    }

    const requestId = `lc-${nextLocalCheckId++}`;
    const timer = window.setTimeout(() => {
      pendingLocalChecks.delete(requestId);
      reject(new Error('the local writing check timed out'));
    }, LOCAL_CHECK_TIMEOUT_MS);

    pendingLocalChecks.set(requestId, (raw) => {
      window.clearTimeout(timer);
      try {
        const parsed = JSON.parse(raw) as LocalCheckResponse<T>;
        if (parsed.ok) {
          resolve(parsed.data as T);
        } else {
          reject(new Error(parsed.error || 'the local writing check failed'));
        }
      } catch {
        reject(new Error('the local writing check returned malformed JSON'));
      }
    });

    try {
      bridge.localCheck(requestId, JSON.stringify(request));
    } catch (error) {
      window.clearTimeout(timer);
      pendingLocalChecks.delete(requestId);
      reject(error);
    }
  });
